import { useEffect } from "react";
import { useUiStore, type Toast } from "@/stores/ui.store";

const typeConfig = {
  success: {
    bg: "bg-emerald-50 border-emerald-200",
    text: "text-emerald-800",
    actionBg: "bg-emerald-600 hover:bg-emerald-700",
    icon: (
      <svg className="h-5 w-5 text-emerald-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    ),
  },
  error: {
    bg: "bg-red-50 border-red-200",
    text: "text-red-800",
    actionBg: "bg-red-600 hover:bg-red-700",
    icon: (
      <svg className="h-5 w-5 text-red-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    ),
  },
  info: {
    bg: "bg-primary-50 border-primary-200",
    text: "text-primary-800",
    actionBg: "bg-primary-600 hover:bg-primary-700",
    icon: (
      <svg className="h-5 w-5 text-primary-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
      </svg>
    ),
  },
  warning: {
    bg: "bg-amber-50 border-amber-200",
    text: "text-amber-800",
    actionBg: "bg-amber-600 hover:bg-amber-700",
    icon: (
      <svg className="h-5 w-5 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
      </svg>
    ),
  },
};

function ToastItem({ toast }: { toast: Toast }) {
  const { dismissToast } = useUiStore();
  const config = typeConfig[toast.type];

  useEffect(() => {
    if (toast.action) return;
    const timer = setTimeout(() => dismissToast(toast.id), 4000);
    return () => clearTimeout(timer);
  }, [toast.id, toast.action, dismissToast]);

  return (
    <div
      className={`${config.bg} ${toast.dismissing ? "animate-toast-out" : "animate-toast-in"} flex items-center gap-3 rounded-xl border p-3.5 shadow-lg shadow-black/5`}
      role="alert"
    >
      {config.icon}
      <p className={`${config.text} flex-1 text-sm font-medium min-w-0`}>
        {toast.message}
      </p>
      {toast.action && (
        <button
          onClick={() => {
            toast.action!.onClick();
            dismissToast(toast.id);
          }}
          className={`${config.actionBg} shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition-colors`}
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={() => dismissToast(toast.id)}
        className="shrink-0 rounded-md p-0.5 text-slate-400 hover:text-slate-600 transition-colors"
        aria-label="Dismiss"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export function ToastContainer() {
  const { toasts } = useUiStore();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-20 left-3 right-3 z-[100] flex flex-col gap-2 lg:bottom-6 lg:left-auto lg:right-6 lg:w-[400px]">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
